import { MouseEvent, ReactElement, useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import { useIsEntityManageTeamAvailable } from '@/api/entity-views/queries';
import AllFundsIcon from '@/assets/svg/sidebar/all-funds.svg';
import BookIcon from '@/assets/svg/sidebar/book.svg';
import BriefcaseBusinessIcon from '@/assets/svg/sidebar/briefcase-business.svg';
import CalendarIcon from '@/assets/svg/sidebar/calendar.svg';
import HandshakeIcon from '@/assets/svg/sidebar/handshake.svg';
import LayoutDashboardIcon from '@/assets/svg/sidebar/layout-dashboard.svg';
import ManageTeamIcon from '@/assets/svg/sidebar/manage-team.svg';
import MessagesSquareIcon from '@/assets/svg/sidebar/messages-square.svg';
import Settings2Icon from '@/assets/svg/sidebar/settings-2.svg';
import UserPlusIcon from '@/assets/svg/sidebar/user-plus.svg';
import UsersIcon from '@/assets/svg/sidebar/users.svg';
// My Profile has no dedicated sidebar glyph yet; it borrows a ui-element icon and is
// tracked as a design-system gap rather than an invented one-off.
import ProfileIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { ENTITY_DEALS_TABS } from '@/pages/deals/entity-deal/common';
import { PERSONAL_DEALS_TABS } from '@/pages/deals/personal/common';
import { PRE_IPO_TABS } from '@/pages/deals/pre-ipo-v2/tabs';
import { overviewTabKeys } from '@/pages/overview/data';
import { portfolioTabs } from '@/pages/portfolio';
import { DEALS_TAB_KEYS } from '@/routes/deals-tabs';
import { PagePaths } from '@/routes/page-paths';
import {
  NON_ACCREDITED_GATED_HREFS,
  NON_ACCREDITED_TOOLTIP,
} from '@/shared/modules/accredited-investor-gate';
import { Permission, useCan } from '@/shared/modules/permissions';
import { UserRole } from '@/stores/slices/selected-user-config';

export type SidebarLink = {
  text: string;
  href: string | null;
  icon: ReactElement;
  tooltip?: string;
  onClick?: (_event: MouseEvent<HTMLAnchorElement>) => void;
  unreadCount?: number;
  /**
   * Sub-navigation rendered beneath this link. One level only — a sub-link never has sub-links of
   * its own, and nothing in the sidebar needs deeper nesting.
   *
   * Deliberately not named `children`: React reserves that prop, and a `SidebarLink[]` under that
   * name both trips `react/no-children-prop` and invites someone to render it as a node.
   */
  subLinks?: SidebarLink[];
};

export type SidebarLinks = SidebarLink[][];

const portfolioHref = `${PagePaths.portfolio}?tab=${portfolioTabs.assets}`;
const gammaDashboardHref = `${PagePaths.overview}?tab=${overviewTabKeys.Users}`;

const contactsLink: SidebarLink = {
  text: 'Contacts',
  href: PagePaths.addressBook,
  icon: <BookIcon />,
};

const chatsLink: SidebarLink = {
  text: 'Chats',
  href: PagePaths.communications,
  icon: <MessagesSquareIcon />,
};

const eventsLink: SidebarLink = {
  text: 'Events',
  href: PagePaths.events,
  icon: <CalendarIcon />,
};

const userSideBarLinks: SidebarLinks = [
  [
    {
      text: 'Portfolio',
      href: portfolioHref,
      icon: <BriefcaseBusinessIcon />,
    },
    {
      text: 'Funds',
      href: PagePaths.allFunds,
      icon: <AllFundsIcon />,
    },
    {
      text: 'Pre-IPO',
      href: `${PagePaths.deals}?filter=${PERSONAL_DEALS_TABS.ALL}`,
      icon: <HandshakeIcon />,
    },
    contactsLink,
    chatsLink,
    eventsLink,
  ],
  [
    {
      text: 'Introducer Program',
      href: PagePaths.introducer,
      icon: <UserPlusIcon />,
    },
  ],
];

const PRE_IPO_V2_HREF = `${PagePaths.deals}?tab=${PRE_IPO_TABS.COMPANIES}`;

/** Points the cabinet's Pre-IPO entry at the v2 default tab instead of a legacy one. */
const withPreIpoV2Href = (links: SidebarLinks): SidebarLinks =>
  links.map((group) =>
    group.map((link) =>
      link.href?.startsWith(`${PagePaths.deals}?`) ? { ...link, href: PRE_IPO_V2_HREF } : link,
    ),
  );

const getBaseLinks = (
  role: UserRole,
  entityId?: string,
  isEntityRejected?: boolean,
): SidebarLinks => {
  switch (role) {
    case 'admin':
      return adminSideBarLinks;
    case 'entity':
      return getEntitySideBarLinks(entityId, isEntityRejected);
    default:
      return userSideBarLinks;
  }
};

const getEntitySideBarLinks = (entityId?: string, isEntityRejected?: boolean): SidebarLinks => [
  [
    {
      text: 'Company Portfolio',
      href: portfolioHref,
      icon: <BriefcaseBusinessIcon />,
    },
    {
      text: 'Company Funds',
      href: PagePaths.fundDashboard,
      icon: <LayoutDashboardIcon />,
    },
    {
      text: 'Investors',
      href: PagePaths.investors,
      icon: <UsersIcon />,
    },
  ],
  [
    {
      text: 'All Funds',
      href: PagePaths.allFunds,
      icon: <AllFundsIcon />,
    },
    {
      text: 'Pre-IPO',
      href: `${PagePaths.deals}?tab=${ENTITY_DEALS_TABS.PROVIDER}`,
      icon: <HandshakeIcon />,
    },
  ],
  [contactsLink, chatsLink, eventsLink],
  [
    {
      text: 'Manage Entity',
      href: entityId ? `${PagePaths.manageEntity}/${entityId}` : null,
      icon: <Settings2Icon />,
    },
    {
      text: 'Manage Team',
      href: !isEntityRejected ? PagePaths.manageTeam : null,
      icon: <ManageTeamIcon />,
      tooltip: isEntityRejected
        ? 'Entity rejected. Please contact Gamma Prime immediately to resolve the issue and regain access to team management.'
        : undefined,
    },
  ],
];

const adminSideBarLinks: SidebarLinks = [
  [
    {
      text: 'Gamma Dashboard',
      href: gammaDashboardHref,
      icon: <LayoutDashboardIcon />,
    },
    {
      text: 'Introducers',
      href: PagePaths.introducer,
      icon: <UserPlusIcon />,
    },
    {
      text: 'Manage Team',
      href: PagePaths.gammaTeamManagement,
      icon: <ManageTeamIcon />,
    },
  ],
  [
    {
      text: 'Funds',
      href: PagePaths.allFunds,
      icon: <AllFundsIcon />,
    },
    {
      text: 'Pre-IPO',
      href: `${PagePaths.deals}?tab=${DEALS_TAB_KEYS.INVESTOR}`,
      icon: <HandshakeIcon />,
    },
  ],
  [contactsLink, chatsLink, eventsLink],
];

/**
 * Your own profile, at the head of the first group (AC30); without it the
 * publishing surface is reachable only by typing a URL.
 */
const withOwnProfile = (links: SidebarLinks, href: string | null): SidebarLinks => {
  if (!href || !links.length) return links;

  const profileLink: SidebarLink = { text: 'My Profile', href, icon: <ProfileIcon /> };
  return [[profileLink, ...links[0]], ...links.slice(1)];
};

type UseGetSidebarLinksType = {
  role: UserRole;
  entityId?: string;
  userId?: string;
  hasSubmittedQuestionnaire?: boolean;
  isBlocked?: boolean;
  blockReason?: string | null;
  kycStatus?: string;
  isEntityRejected?: boolean;
  isNonAccredited?: boolean;
};

const LINKS_WHITE_LIST_FOR_BLOCKED: string[] = [portfolioHref, PagePaths.communications];

const getIntroducerTooltip = (kycApproved: boolean, questionnaireSubmitted: boolean): string => {
  if (!kycApproved && !questionnaireSubmitted) {
    return "Please complete KYC verification and the Investor's Questionnaire to access the Introducer Program.";
  }
  if (!kycApproved) {
    return 'Please complete KYC verification to access the Introducer Program.';
  }
  return "Please complete the Investor's Questionnaire to access the Introducer Program.";
};

export const useGetSidebarLinks = ({
  role,
  entityId,
  userId,
  hasSubmittedQuestionnaire,
  isBlocked,
  blockReason,
  kycStatus,
  isEntityRejected,
  isNonAccredited,
}: UseGetSidebarLinksType): SidebarLinks => {
  const { available: isEntityManageTeamAvailable } = useIsEntityManageTeamAvailable(entityId);
  const isUserProfileV2Enabled = useFeatureFlag(FeatureFlags.USER_PROFILE_V2);
  const isEventsOpen = useFeatureFlag(FeatureFlags.GAMMA_EVENTS_ENABLED);
  const isAdminPreIpoV2 = useFeatureFlag(FeatureFlags.PREIPO_V2_ADMIN);
  const isEntityPreIpoV2 = useFeatureFlag(FeatureFlags.PREIPO_V2_ENTITY);
  const canViewAdminConsole = useCan(Permission.AdminConsoleView);
  const canViewGammaTeamRoster = useCan(Permission.GammaTeamView);
  const canViewGammaRoles = useCan(Permission.GammaViewRoles);
  const canApproveGammaRoleRequests = useCan(Permission.GammaApproveRoleRequests);
  const canViewGammaTeam =
    canViewGammaTeamRoster || canViewGammaRoles || canApproveGammaRoleRequests;
  const canViewIntroducerAdmin = useCan(Permission.ReferralAdminView);

  return useMemo(() => {
    const baseRoleLinks = getBaseLinks(
      role,
      entityId,
      isEntityRejected || !isEntityManageTeamAvailable,
    );
    const usesPreIpoV2 =
      (role === 'admin' && isAdminPreIpoV2) || (role === 'entity' && isEntityPreIpoV2);
    const roleLinks = usesPreIpoV2 ? withPreIpoV2Href(baseRoleLinks) : baseRoleLinks;
    // Personal cabinets only — an Entity has no page on `/user/:id`; its own page is
    // `/address-book/entity/:id` and carries no publishing surface.
    const ownProfileHref =
      role === 'entity' || !userId ? null : generatePath(PagePaths.userProfile, { id: userId });
    const rawLinks = isUserProfileV2Enabled ? withOwnProfile(roleLinks, ownProfileHref) : roleLinks;
    // Only the admin cabinet: `/introducer` is also the investor's own Introducer Program link,
    // which no gamma permission governs.
    const isAdminCabinet = role === 'admin';
    const hiddenHrefs: string[] = [
      ...(isEventsOpen ? [] : [PagePaths.events]),
      ...(isAdminCabinet && !canViewAdminConsole ? [gammaDashboardHref] : []),
      ...(isAdminCabinet && !canViewGammaTeam ? [PagePaths.gammaTeamManagement] : []),
      ...(isAdminCabinet && !canViewIntroducerAdmin ? [PagePaths.introducer] : []),
    ];
    const baseLinks = hiddenHrefs.length
      ? rawLinks
          .map((group) => group.filter((link) => !hiddenHrefs.includes(link.href ?? '')))
          // Sidebar renders a Separator per non-first group, so an emptied group leaves a stray divider.
          .filter((group) => group.length > 0)
      : rawLinks;
    const kycApproved = kycStatus === 'APPROVED';
    const questionnaireSubmitted = !!hasSubmittedQuestionnaire;
    const disableLinks = !questionnaireSubmitted || !!isBlocked;
    const introducerDisabled = role === 'user' && (!kycApproved || !questionnaireSubmitted);
    // Accreditation gate applies only to personal accounts — admin/entity sessions are exempt.
    const accreditationGated = role === 'user' && !!isNonAccredited;

    if (!disableLinks && !introducerDisabled && !accreditationGated) return baseLinks;

    return baseLinks.map((linkGroup) =>
      linkGroup.map((link) => {
        if (accreditationGated) {
          const baseHref = link.href?.split(/[?#]/)[0] ?? '';
          if (NON_ACCREDITED_GATED_HREFS.includes(baseHref)) {
            return { ...link, href: null, tooltip: NON_ACCREDITED_TOOLTIP };
          }
        }

        if (disableLinks) {
          const isWhitelisted = !!link.href && LINKS_WHITE_LIST_FOR_BLOCKED.includes(link.href);
          return {
            ...link,
            href: isWhitelisted ? link.href : null,
            tooltip: isBlocked
              ? `Your profile is blocked${blockReason ? `. Reason: ${blockReason}` : ''}`
              : !hasSubmittedQuestionnaire
                ? "Please complete the Investor's Questionnaire to access this feature."
                : link.tooltip,
          };
        }

        if (introducerDisabled && link.href === PagePaths.introducer) {
          return {
            ...link,
            href: null,
            tooltip: getIntroducerTooltip(kycApproved, questionnaireSubmitted),
          };
        }

        return link;
      }),
    );
  }, [
    role,
    entityId,
    hasSubmittedQuestionnaire,
    isBlocked,
    blockReason,
    isUserProfileV2Enabled,
    userId,
    canViewAdminConsole,
    canViewGammaTeam,
    canViewIntroducerAdmin,
    isEventsOpen,
    isAdminPreIpoV2,
    isEntityPreIpoV2,
    isEntityManageTeamAvailable,
    isEntityRejected,
    kycStatus,
    isNonAccredited,
  ]);
};
